import { FC, useEffect, useState } from "react";
import {
  appointment_package,
  appointment_product,
  appointment_service,
  appointmentDetails,
  availableTeamMember,
} from "../../../../types/calendar";
import AppointmentClientDetails from "./AppointmentClientDetails";
import AppointmentServicesDetails from "./AppointmentServicesDetails";
import AppointmentModalHeader from "./AppointmentModalHeader";
import ReservationDetails from "./ReservationDetails";
import { useMutation, useQuery } from "@tanstack/react-query";
import { serviceAndProductsSVG } from "../../../../assets/SVGs/SVGs";
import AppointmentModalFooter from "./AppointmentModalFooter";
import { useIntl } from "react-intl";

import { Button, message, Spin } from "antd";
import {
  adminUpdateAppointment,
  getAppointmentDetails,
} from "../../../../network/admin";
import queryClient from "../../../../services/react-query/queryClient";
import dayjs from "dayjs";
import {
  getAppointment,
  updateAppointment,
} from "../../../../network/calendar";
import { useSelector } from "react-redux";

interface AppointmentDetailsModalWrapperProps {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  appointment: appointmentDetails;
  editMode?: boolean;
  isAdmin?: boolean;
}

const AppointmentDetailsModalWrapper: FC<
  AppointmentDetailsModalWrapperProps
> = ({ isAdmin, appointment, setIsModalOpen, editMode }) => {
  const intl = useIntl();
  const [services, setServices] = useState<appointment_service[]>([]);
  const [packages, setPackages] = useState<appointment_package[]>([]);
  const [products, setProducts] = useState<appointment_product[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const { locationId } = useSelector(
    (state: {
      calendar: {
        locationId: number;
      };
    }) => state.calendar
  );
  const { data, isLoading } = useQuery(
    ["appointment", appointment?.id],
    () => {
      if (isAdmin) {
        return getAppointmentDetails(appointment?.id);
      } else {
        return getAppointment(locationId, appointment?.id);
      }
    },
    {
      enabled: !!appointment?.id,
      select: (res) => res?.data?.data || res?.data,
    }
  );
  const { mutate, isLoading: isUpdating } = useMutation(
    (body: any) => {
      if (isAdmin) {
        return adminUpdateAppointment(data?.location_id, data?.id, body);
      } else {
        return updateAppointment(data?.location_id, appointment?.id, body);
      }
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          queryClient.invalidateQueries(["appointment", appointment?.id]);
          queryClient.invalidateQueries(["appointments"]);
          message.success(intl.formatMessage({ id: "updatedSuccessfully" }));
        } else {
          message.error(
            res?.response?.data?.message ||
              intl.formatMessage({
                id: "somethingWentWrong",
                defaultMessage: "حدث خطأ",
              })
          );
        }
      },
    }
  );
  const handleUpdateAppointment = () => {
    const startTime = data?.start_time?.split(":");
    const bodyData: any = {
      start_time: [startTime[0], startTime[1]].join(":"),

      date: data?.date,
      services: services?.map((s) => ({
        pricing_option_id: s?.pricing_option?.id,
        team_member_id: s?.team_member?.id,
      })),
      packages: packages?.map((pkg) => ({
        package_id: pkg?.package?.id,
        team_member_id: pkg?.team_member?.id,
      })),
      products_ids: products?.map((p) => {
        return { id: p?.id, quantity: p?.pivot?.quantity };
      }),
      payment_method_id: paymentMethod?.id,
    };
    if (appointment?.loyalty_program_range) {
      bodyData.client_loyalty_id = appointment?.loyalty_program_range?.id;
    }
    if (appointment?.taxable) {
      bodyData.taxable_id = appointment?.taxable?.id;
      bodyData.taxable_type = "tax";
    }
    if (appointment?.discount && Number(appointment?.discount) > 0) {
      bodyData.discount = Number(appointment?.discount);
      bodyData.discount_type = appointment?.discount_type;
    }
    mutate(bodyData);
  };
  useEffect(() => {
    if (data) {
      setPaymentMethod(data?.payment_method || null);
      const newServices: appointment_service[] =
        data?.appointment_services?.map((service: any) => {
          if (editMode) {
            return {
              ...service,
              editable: true,
            };
          } else {
            return service;
          }
        });
      const newPackages: appointment_package[] =
        data?.appointment_packages?.map((packageItem: any) => {
          if (editMode) {
            return {
              ...packageItem,
              editable: true,
            };
          } else {
            return packageItem;
          }
        });
      const newProducts: appointment_product[] = data?.products?.map(
        (product: any) => {
          if (editMode) {
            return {
              ...product,
              editable: true,
            };
          } else {
            return product;
          }
        }
      );

      setServices(newServices);
      setPackages(newPackages);
      setProducts(newProducts);
    }
  }, [data, appointment?.id]);
  if (isLoading) {
    return (
      <div className="w-full h-full p-4 text-center">
        <Spin size="large" />
      </div>
    );
  }
  if (!data?.date) {
    return (
      <div>
        <h2>
          {intl.formatMessage({
            id: "noAppointmentExists",
            defaultMessage: "لا يوجد حجز",
          })}
        </h2>
      </div>
    );
  }
  const changeEditMode = (
    id: number,
    value: boolean,
    type: "service" | "package" | "product"
  ) => {
    if (type === "service") {
      const newServices: appointment_service[] = services.map((service) => {
        if (service.id === id) {
          return {
            ...service,
            editable: value,
          };
        } else {
          return service;
        }
      });
      setServices(newServices);
    }
    if (type === "package") {
      const newPackages: appointment_package[] = packages.map((packageItem) => {
        if (packageItem.id === id) {
          return {
            ...packageItem,
            editable: value,
          };
        } else {
          return packageItem;
        }
      });
      setPackages(newPackages);
    }
    if (type === "product") {
      const newProducts: appointment_product[] = products.map((product) => {
        if (product.id === id) {
          return {
            ...product,
            editable: value,
          };
        } else {
          return product;
        }
      });
      setProducts(newProducts);
    }
  };
  const changeTeamMember = (
    id: number,
    teamMember: availableTeamMember,
    type: "service" | "package"
  ) => {
    if (type === "service") {
      const newServices: appointment_service[] = services.map((service) => {
        if (service.id === id) {
          console.log(service, teamMember?.id, "service Before");
          return {
            ...service,
            team_member: teamMember,
          };
        } else {
          return service;
        }
      });
      setServices(newServices);
    }
    if (type === "package") {
      const newPackages: appointment_package[] = packages.map((packageItem) => {
        if (packageItem.id === id) {
          console.log(packageItem, teamMember, "packageItem Before");
          return {
            ...packageItem,
            team_member: teamMember,
          };
        } else {
          return packageItem;
        }
      });
      setPackages(newPackages);
    }
  };
  const deleteService = (id: number) => {
    const newServices: appointment_service[] = services.filter((service) => {
      if (service.id === id) {
        return false;
      } else {
        return true;
      }
    });
    setServices(newServices);
  };
  const deletePackage = (id: number) => {
    const newPackages: appointment_package[] = packages.filter(
      (packageItem) => {
        if (packageItem.id === id) {
          return false;
        } else {
          return true;
        }
      }
    );
    setPackages(newPackages);
  };
  const deleteProduct = (id: number) => {
    const newProducts: appointment_product[] = products.filter((product) => {
      if (product.id === id) {
        return false;
      } else {
        return true;
      }
    });
    setProducts(newProducts);
  };
  const handleChangeProductQty = (
    productId: number,
    type: "increase" | "decrease"
  ) => {
    if (type === "increase") {
      const newProducts = products.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            pivot: {
              ...product?.pivot,
              quantity: String(Number(product?.pivot?.quantity) + 1),
            },
          };
        }
        return product;
      });
      setProducts(newProducts);
    } else if (type === "decrease") {
      const newProducts: appointment_product[] = products.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            pivot: {
              ...product?.pivot,
              quantity: String(Number(product?.pivot?.quantity) - 1),
            },
          };
        }
        return product;
      });
      setProducts(newProducts);
    }
  };
  const canEdit = (): boolean => {
    if (data?.status === "canceled" || data?.status === "completed") {
      return false;
    }
    const currentDate = new Date();
    const appointmentDate = new Date(data?.date + " " + data?.start_time);
    if (dayjs(appointmentDate).isAfter(currentDate)) {
      return true;
    } else {
      return false;
    }
  };
  return (
    <div className="my-2 py-2 space-y-4">
      <AppointmentModalHeader
        dateAndTime={{
          date: new Date(data?.date),
          startTime: data?.start_time,
          finishTime: data?.finish_time,
        }}
        status={data?.status}
        appointment={data}
        canEdit={canEdit()}
        isAdmin={isAdmin}
      />
      <div className="flex justify-center items-center gap-1 ">
        <span>{serviceAndProductsSVG}</span>
        <p>
          {intl.formatMessage({
            id: "appointmentDetails",
            defaultMessage: "تفاصيل الحجز",
          })}
        </p>
      </div>
      <AppointmentClientDetails client={data?.appointmentable} />
      <AppointmentServicesDetails
        packages={packages}
        products={products}
        services={services}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        appointment={data}
        changeEditMode={changeEditMode}
        changeTeamMember={changeTeamMember}
        deleteService={deleteService}
        deletePackage={deletePackage}
        handleChangeProductQty={handleChangeProductQty}
        deleteProduct={deleteProduct}
        isAdmin={isAdmin}
        loyaltyDiscount={appointment?.loyalty_program_range}
      />
      <ReservationDetails appointment={data} />
      <AppointmentModalFooter
        closeModal={() => setIsModalOpen(false)}
        appointment={data}
        canEdit={canEdit()}
        isAdmin={isAdmin}
      />
      {canEdit() ? (
        <div className="flex justify-center gap-4">
          <Button
            htmlType="button"
            block
            type="primary"
            loading={isUpdating}
            onClick={handleUpdateAppointment}
          >
            {intl.formatMessage({ id: "saveChanges" })}
          </Button>
          <Button
            htmlType="button"
            type="default"
            block
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            {intl.formatMessage({ id: "cancel", defaultMessage: "الغاء" })}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default AppointmentDetailsModalWrapper;
